import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Box, Typography, Grid, FormHelperText } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';

import {
  FBoxContainer,
  FHAutocomplete,
  FHButton,
  FHConfirmDialog,
  FHPageLink,
  FProgressBar,
  FSearch,
} from '../../components/base';
import PageContainer from '../../components/layout/PageContainer';
import CohortAssignmentRow from '../components/CohortAssignmentRow';
import { cohortActions } from '../../store';
import { FORTA_BLUE, FORTA_ERROR, FORTA_WHITE } from '../../utils/colors';
import { history } from '../../helpers';
import { CohortAssignmentMode, Themes } from '../../constants';
import EditOutlinedIcon from '../../components/svgs/EditOutlinedIcon';
import TrashIcon from '../../components/svgs/TrashIcon';

import type { RootState } from '../../store';
import type { ChangeEvent } from 'react';
import type { IClientAssignment } from '../../utils/types';

function CohortDetailsPage() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { cohortId } = useParams();

  const {
    current: currentCohort,
    cohortServiceClasses,
    loading: cohortsLoading,
  } = useSelector((state: RootState) => state.cohorts);
  const [cohortEditable, setCohortEditable] = useState<boolean>(false);
  const [forceDelete, setForceDelete] = useState<boolean>(false);
  const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);
  const [showForceDeletePopup, setShowForceDeletePopup] = useState<boolean>(false);
  const [newAssignmentAdding, setNewAssignmentAdding] = useState<boolean>(false);
  const [clientAssignments, setClientAssignments] = useState<Array<IClientAssignment>>([]);
  const [editableAssignmentIndex, setEditableAssignmentIndex] = useState<number | null>(null);
  const [searchStr, setSearchStr] = useState<string>('');

  useEffect(() => {
    if (!cohortId) return;
    dispatch(cohortActions.getCohortServiceClasses({ getAccessTokenSilently }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    service_classes: Yup.array().min(1, 'At least one service class is required'),
  });

  const formik = useFormik({
    initialValues: {
      name: currentCohort?.name || '',
      service_classes: currentCohort?.service_classes || [],
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const response = await dispatch(
        cohortActions.updateCohort({
          cohortId: cohortId,
          data: {
            name: values.name,
            service_classes: values.service_classes,
          },
          getAccessTokenSilently,
        }),
      );

      if (!response.error) {
        setCohortEditable(false);
        dispatch(cohortActions.getCohortById({ cohortId, getAccessTokenSilently }));
        dispatch(cohortActions.getList({ getAccessTokenSilently }));
      }
    },
  });

  const { setFieldValue, submitForm, touched, errors, values } = formik;

  const handleUpdateFieldValues = (key: string, value: any) => {
    setFieldValue(key, value);
  };

  const handleUpdateSearch = (searchStr: string) => {
    setSearchStr(searchStr);
    if (searchStr.length > 0) {
      setEditableAssignmentIndex(null);
      setNewAssignmentAdding(false);
      const filteredAssignments = currentCohort?.client_assignments.filter(
        (assignment) =>
          assignment.client.first_name.toLowerCase().includes(searchStr.toLowerCase()) ||
          assignment.client.last_name.toLowerCase().includes(searchStr.toLowerCase()),
      );
      setClientAssignments(filteredAssignments || []);
    } else {
      setClientAssignments(currentCohort?.client_assignments || []);
    }
  };

  useEffect(() => {
    if (!cohortId) return;
    dispatch(cohortActions.getCohortById({ cohortId, getAccessTokenSilently }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohortId]);

  useEffect(() => {
    if (!cohortId) return;
    if (currentCohort) {
      setFieldValue('name', currentCohort.name);
      setFieldValue('service_classes', currentCohort.service_classes);
      setClientAssignments(currentCohort.client_assignments || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCohort, cohortId]);

  const handleDeleteCohort = async () => {
    const response = await dispatch(
      cohortActions.deleteCohort({
        cohortId: cohortId,
        forceDelete,
        getAccessTokenSilently,
      }),
    );
    if (!response.error) {
      dispatch(cohortActions.getList({ getAccessTokenSilently }));
      history.navigate('/admin');
    }
  };

  const getForceDeleteStatus = () => {
    return currentCohort?.client_assignments.length > 0 ? true : false;
  };

  const handleAddAssignment = () => {
    const newAssignment = {
      client: {
        first_name: '',
        last_name: '',
        middle_name: '',
        sex: '',
        dob: '',
        email: '',
        street_address_line_one: '',
        street_address_line_two: '',
        street_address_zip: '',
        street_address_city: '',
        street_address_state: '',
        phone: '',
      },
      cohort: {
        id: currentCohort?.id,
        name: currentCohort?.name,
        service_classes: currentCohort?.service_classes,
        client_assignments: currentCohort?.client_assignments,
      },
      date_start: '',
      date_end: null,
    };

    setClientAssignments([newAssignment, ...clientAssignments]);
    setEditableAssignmentIndex(0);
    setNewAssignmentAdding(true);
  };

  const handleCancelAssignment = () => {
    setClientAssignments(currentCohort?.client_assignments || []);
    setEditableAssignmentIndex(null);
    setNewAssignmentAdding(false);
  };

  const handleAssignClient = async (assignment: IClientAssignment) => {
    try {
      const response = assignment.id
        ? await dispatch(
            cohortActions.updateClientCohortAssignment({
              assignmentId: assignment.id,
              data: {
                cohort_id: assignment.cohort?.id,
                client_id: assignment.client.id,
                date_start: moment(assignment.date_start).format('YYYY-MM-DD'),
                date_end: assignment.date_end
                  ? moment(assignment.date_end).format('YYYY-MM-DD')
                  : null,
              },
              getAccessTokenSilently,
            }),
          )
        : await dispatch(
            cohortActions.createClientCohortAssignment({
              data: {
                cohort_id: assignment.cohort?.id,
                client_id: assignment.client.id,
                date_start: moment(assignment.date_start).format('YYYY-MM-DD'),
                date_end: assignment.date_end
                  ? moment(assignment.date_end).format('YYYY-MM-DD')
                  : null,
              },
              getAccessTokenSilently,
            }),
          );
      if (!response.error) {
        dispatch(cohortActions.getCohortById({ cohortId, getAccessTokenSilently }));
        setEditableAssignmentIndex(null);
        setNewAssignmentAdding(false);
        setSearchStr('');
      }
    } catch (error) {
      toast.error(`Error updating/creating contract:${error}`);
    }
  };
  const handleDeleteAssignment = async (assignment: IClientAssignment) => {
    try {
      const response = await dispatch(
        cohortActions.deleteClientCohortAssignment({
          assignmentId: assignment.id,
          getAccessTokenSilently,
        }),
      );
      if (!response.error) {
        dispatch(cohortActions.getCohortById({ cohortId, getAccessTokenSilently }));
      }
    } catch (error) {
      toast.error(`Error deleting contract:${error}`);
    }
  };

  const handleCohortCancel = () => {
    setFieldValue('name', currentCohort.name);
    setFieldValue('service_classes', currentCohort.service_classes);
    setCohortEditable(!cohortEditable);
  };

  const AssignmentsTableHeader = () => (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Grid container spacing={1} mb={1}>
        <Grid item xs={3.2}>
          <Typography textAlign={'left'} fontWeight="bold">
            Client*
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography textAlign={'left'} fontWeight="bold">
            Start date*
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography textAlign={'left'} fontWeight="bold">
            End date
          </Typography>
        </Grid>
        <Grid item xs={4}></Grid>
      </Grid>
    </Box>
  );

  return (
    <PageContainer>
      {cohortsLoading && <FProgressBar />}
      <Box sx={{ mt: 3 }} textAlign={'left'}>
        <FHPageLink to="/admin" label="Admin Home" />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h3">{currentCohort.name}</Typography>
          </Box>
          <Box>
            <FHButton
              startIcon={<TrashIcon color={FORTA_WHITE} width={20} />}
              onClick={() => {
                setShowDeletePopup(true);
              }}
            >
              Delete Cohort
            </FHButton>
          </Box>
        </Box>

        <hr></hr>
        <Box mb={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} lg={12} mt={4}>
              <FBoxContainer sx={{ height: '100%', borderRadius: '20px', boxShadow: 'none' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <Typography variant="h6" mb={2} fontWeight={'bold'}>
                      Cohort Info
                    </Typography>
                    {!cohortEditable && (
                      <FHButton
                        lightBackground
                        sx={{
                          border: `2px solid ${FORTA_BLUE.primary}`,
                          color: FORTA_BLUE.primary,
                        }}
                        startIcon={<EditOutlinedIcon width={16} color={FORTA_BLUE.primary} />}
                        onClick={() => {
                          setCohortEditable(!cohortEditable);
                        }}
                      >
                        Edit cohort details
                      </FHButton>
                    )}
                  </Box>
                  <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
                    <Grid container p={0.5} spacing={1} direction="row" alignItems="flex-start">
                      <Grid item xs={2}>
                        <Typography
                          sx={{
                            fontWeight: 'bold',
                          }}
                        >
                          Cohort name:
                        </Typography>
                      </Grid>
                      <Grid item xs={7}>
                        <Typography>{values.name}</Typography>
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      mt={1}
                      p={0.5}
                      spacing={1}
                      direction="row"
                      alignItems="flex-start"
                    >
                      <Grid item xs={2}>
                        <Typography sx={{ fontWeight: 'bold' }}>Service classes:</Typography>
                      </Grid>
                      <Grid item xs={7}>
                        {cohortEditable ? (
                          <>
                            <FHAutocomplete
                              multiple
                              sx={{
                                width: '100%',
                                '& .MuiOutlinedInput-input': {
                                  minHeight: '40px',
                                },
                              }}
                              placeholder="Choose service class"
                              options={cohortServiceClasses}
                              value={values.service_classes}
                              onChange={(_event: ChangeEvent, newValue: string[]) => {
                                handleUpdateFieldValues('service_classes', newValue || []);
                              }}
                              error={touched.service_classes && Boolean(errors.service_classes)}
                            />
                            {touched.service_classes && errors.service_classes && (
                              <FormHelperText
                                sx={{ color: FORTA_ERROR, textAlign: 'left', fontSize: '14px' }}
                              >
                                {Array.isArray(errors.service_classes)
                                  ? errors.service_classes.join(', ')
                                  : errors.service_classes}
                              </FormHelperText>
                            )}
                          </>
                        ) : (
                          <Typography>
                            {values.service_classes &&
                              values.service_classes.map((sc) => sc).join(', ')}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      mt={1}
                      p={0.5}
                      spacing={1}
                      direction="row"
                      alignItems="flex-start"
                    >
                      <Grid item xs={2}></Grid>
                      <Grid item xs={1} mr={2}>
                        {cohortEditable && (
                          <FHButton onClick={submitForm} data-testid="saveProvider">
                            Submit
                          </FHButton>
                        )}
                      </Grid>
                      <Grid item xs={3}>
                        {cohortEditable && (
                          <FHButton
                            lightBackground
                            sx={{
                              border: `2px solid ${FORTA_BLUE.primary}`,
                              color: FORTA_BLUE.primary,
                            }}
                            onClick={handleCohortCancel}
                          >
                            Cancel
                          </FHButton>
                        )}
                      </Grid>
                    </Grid>
                  </form>
                </Box>
              </FBoxContainer>
            </Grid>

            <Grid item xs={12} lg={12} mt={4}>
              <FBoxContainer sx={{ height: '100%', borderRadius: '20px', boxShadow: 'none' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <Typography variant="h6" mb={2} fontWeight={'bold'}>
                      Assigned clients
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <FHButton
                      sx={{
                        width: 250,
                      }}
                      startIcon={<AddIcon />}
                      disabled={newAssignmentAdding}
                      onClick={handleAddAssignment}
                    >
                      Add new assignment
                    </FHButton>
                    <FSearch
                      sx={{
                        borderRadius: '8px',
                        mr: 3.5,
                        mb: 2,
                        backgroundColor: FORTA_WHITE,
                        boxShadow: 'none',
                      }}
                      value={searchStr}
                      autoComplete="off"
                      inputHeight={40}
                      placeholder="Client name"
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleUpdateSearch(e.target.value)
                      }
                    />
                  </Box>
                  <Grid container p={0.5} spacing={1} direction="row" mt={2}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', px: 2 }}>
                      {clientAssignments.length > 0 ? (
                        <>
                          <AssignmentsTableHeader />
                          {clientAssignments.map((assignment, index) => (
                            <CohortAssignmentRow
                              key={index}
                              clientAssignment={assignment}
                              isAdding={newAssignmentAdding && index === 0}
                              isEditing={editableAssignmentIndex === index}
                              isDisabled={cohortsLoading}
                              onEdit={() => {
                                setEditableAssignmentIndex(index);
                              }}
                              onCancel={handleCancelAssignment}
                              onSave={handleAssignClient}
                              onDelete={handleDeleteAssignment}
                              mode={CohortAssignmentMode.Cohort}
                            />
                          ))}
                        </>
                      ) : (
                        <Grid item xs={12}>
                          <Typography
                            sx={{
                              p: 1,
                              textAlign: 'center',
                            }}
                          >
                            No clients assigned to this cohort
                          </Typography>
                        </Grid>
                      )}
                    </Box>
                  </Grid>
                </Box>
              </FBoxContainer>
            </Grid>
          </Grid>
        </Box>
      </Box>
      <FHConfirmDialog
        open={showDeletePopup}
        colorTheme={Themes.Primary}
        title={'Delete Cohort'}
        content={'Are you sure you want to delete this cohort ? This action cannot be undone.'}
        submittext={'Yes, Delete'}
        canceltext={'Cancel'}
        onClose={() => setShowDeletePopup(false)}
        onSubmit={() => {
          if (getForceDeleteStatus()) {
            setForceDelete(true);
            setShowDeletePopup(false);
            setShowForceDeletePopup(true);
          } else {
            handleDeleteCohort();
          }
        }}
      />
      <FHConfirmDialog
        open={showForceDeletePopup}
        colorTheme={Themes.Primary}
        title={'Force delete Cohort'}
        content={
          'This cohort is currently assigned to some clients. Are you certain you want to proceed with force-deleting this cohort?'
        }
        submittext={'Yes, Force delete'}
        canceltext={'Cancel'}
        onClose={() => setShowForceDeletePopup(false)}
        onSubmit={() => {
          setForceDelete(true);
          handleDeleteCohort();
        }}
      />
    </PageContainer>
  );
}

export default CohortDetailsPage;
